import type { EmployeeEvolution } from '@/api/performance-profile/careerGrowthService';

/**
 * What the summary owes the employee about their competency levels:
 *
 * - `levels`: nothing is held back, the two sections speak for themselves;
 * - `levelsAndWait`: an assessment waits for its meeting over levels an earlier cycle already
 *   assessed -- those stay, and the wait is said in one line rather than drawn over them;
 * - `wait`: an assessment waits and no earlier cycle left a level behind, so there is nothing to
 *   show but the wait itself;
 * - `firstEvaluation`: a profile lists competencies, none of them has ever been assessed and
 *   nothing is waiting either -- no one has evaluated the employee yet;
 * - `awaitingProfile`: no competency exists to assess at all, so the employee stands one stop
 *   earlier on that same path -- what they are waiting for is the profile itself.
 */
export type CompetencyView =
    | 'levels'
    | 'levelsAndWait'
    | 'wait'
    | 'firstEvaluation'
    | 'awaitingProfile';

/**
 * Whether the summary is holding levels back until a meeting -- drawn as the path in place of the
 * sections, or as the line above them. The two views differ in what else they show, not in that.
 */
export const areLevelsWithheld = (view: CompetencyView): boolean =>
    view === 'wait' || view === 'levelsAndWait';

/** Every competency the journey carries, whichever of the two profiles it belongs to. */
const journeySkills = ({ currentProfile, desiredProfile }: EmployeeEvolution) => [
    ...(currentProfile?.skills ?? []),
    ...(desiredProfile?.skills ?? []),
];

/** Whether an assessment has anything of the employee's to assess: a profile that lists competencies. */
const hasAssessableProfile = ({ currentProfile }: EmployeeEvolution) =>
    (currentProfile?.skills.length ?? 0) > 0;

/**
 * Which of the five the journey calls for. A level present here is one an earlier cycle left and
 * the employee may read: the server only ever sends the levels their company lets them see, so no
 * level anywhere and no assessment waiting means none was ever given.
 *
 * A journey that carries no competency at all -- no profile assigned, or one the referential has
 * emptied -- is neither of the two waits: no assessment would change what it shows, and promising
 * one would name the wrong cause. Nor is it a state to draw two empty sections over; what that
 * employee waits for is their profile, and the path to their first levels says so by opening on it.
 */
export const readCompetencyView = (evolution: EmployeeEvolution): CompetencyView => {
    const skills = journeySkills(evolution);

    if (skills.length === 0) {
        return 'awaitingProfile';
    }

    const isAssessed = skills.some((skill) => skill.proficiency !== null);

    if (evolution.withheldEvaluation !== null) {
        return isAssessed ? 'levelsAndWait' : 'wait';
    }

    return !isAssessed && hasAssessableProfile(evolution) ? 'firstEvaluation' : 'levels';
};
